import React, { useEffect, useState } from 'react';
import {
  getConsolidatedDrafts, convertDraftsToContainer, assignDraftSupplier,
} from '../lib/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Buildings, Truck, ArrowsClockwise, CheckCircle, Warning, Package, Calendar,
} from '@phosphor-icons/react';
import { toast } from 'sonner';


export default function ConsolidatedDraftsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [converting, setConverting] = useState(null);
  const [etaDays, setEtaDays] = useState({});
  const [assignModal, setAssignModal] = useState(null);
  const [newSupplier, setNewSupplier] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await getConsolidatedDrafts({ status: 'pending_review' });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load drafts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const convert = async (bucket) => {
    if (bucket.supplier_name === 'Unassigned') {
      toast.error('Assign a supplier to these drafts first');
      return;
    }
    setConverting(bucket.supplier_name);
    try {
      const r = await convertDraftsToContainer({
        supplier_name: bucket.supplier_name,
        draft_ids: bucket.lines.map(l => l.id),
        eta_days_from_now: etaDays[bucket.supplier_name] || 30,
        notes: `Consolidated ${bucket.line_count} drafts for ${bucket.supplier_name}`,
      });
      toast.success(`Container ${r.data.container_number} planned for ${bucket.supplier_name} · ${r.data.total_units} units`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Conversion failed');
    } finally {
      setConverting(null);
    }
  };

  const openAssign = (draftId) => {
    setAssignModal(draftId);
    setNewSupplier('');
  };

  const saveAssign = async () => {
    if (!newSupplier.trim()) {
      toast.error('Supplier name required');
      return;
    }
    try {
      await assignDraftSupplier(assignModal, newSupplier.trim());
      toast.success(`Assigned to ${newSupplier.trim()}`);
      setAssignModal(null);
      setNewSupplier('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  if (loading) {
    return <div className="p-6 text-sm opacity-60" data-testid="consolidate-loading">Loading consolidated drafts…</div>;
  }
  if (!data || data.draft_count === 0) {
    return (
      <div className="p-10 text-center" data-testid="consolidate-empty">
        <Package size={48} className="mx-auto mb-3 opacity-30" />
        <p className="text-muted-foreground text-sm">No draft POs pending.</p>
        <p className="text-xs opacity-50 mt-1">Drafts appear here after being promoted from What-If or MRP.</p>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4" data-testid="consolidate-tab">
      {/* Grand totals */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="p-4 rounded-none border border-border">
          <div className="text-[10px] uppercase tracking-wider opacity-60 font-mono">Pending drafts</div>
          <div className="text-2xl font-bold mt-1 tabular-nums" data-testid="kpi-draft-count">
            {data.draft_count}
          </div>
        </Card>
        <Card className="p-4 rounded-none border border-border">
          <div className="text-[10px] uppercase tracking-wider opacity-60 font-mono">Suppliers</div>
          <div className="text-2xl font-bold mt-1 tabular-nums">
            {data.suppliers.length}
          </div>
        </Card>
        <Card className="p-4 rounded-none border border-border">
          <div className="text-[10px] uppercase tracking-wider opacity-60 font-mono">Total units (post-MOQ)</div>
          <div className="text-2xl font-bold mt-1 tabular-nums">
            {data.grand_total_qty}
          </div>
        </Card>
        <Card className="p-4 rounded-none border-2 border-brand bg-blue-50/40">
          <div className="text-[10px] uppercase tracking-wider opacity-60 font-mono">Grand total cost</div>
          <div className="text-2xl font-bold mt-1 tabular-nums" data-testid="kpi-grand-cost">
            ${data.grand_total_cost.toFixed(2)}
          </div>
        </Card>
      </div>

      <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider opacity-60">
        <Buildings size={14} /> Grouped by preferred supplier · MOQ-rounded · oldest order-by first
      </div>

      {/* One card per supplier */}
      <div className="space-y-4">
        {data.suppliers.map(bucket => {
          const unassigned = bucket.supplier_name === 'Unassigned';
          return (
            <Card
              key={bucket.supplier_name}
              className={`rounded-none border-2 ${unassigned ? 'border-amber-300 bg-amber-50/30' : 'border-border'}`}
              data-testid={`vendor-card-${bucket.supplier_name.replace(/\s+/g, '_')}`}
            >
              {/* Vendor header */}
              <div className="p-4 border-b border-border flex items-center gap-3 flex-wrap">
                <div className={`w-10 h-10 rounded-none flex items-center justify-center ${unassigned ? 'bg-amber-500 text-white' : 'bg-brand text-white'}`}>
                  <Buildings size={18} weight="duotone" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-bold">
                      {bucket.supplier_name}
                    </h3>
                    {unassigned && (
                      <span className="text-[10px] uppercase font-mono bg-amber-100 text-amber-800 px-2 py-0.5">
                        <Warning size={10} className="inline mr-0.5" /> needs supplier
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] opacity-70 font-mono mt-0.5">
                    {bucket.line_count} line{bucket.line_count !== 1 ? 's' : ''} ·
                    {bucket.total_qty} units ·
                    ${bucket.total_cost.toFixed(2)}
                    {bucket.earliest_order_by && (
                      <>
                        {' · '}
                        <Calendar size={11} className="inline" /> order by {bucket.earliest_order_by}
                      </>
                    )}
                  </div>
                </div>
                {!unassigned && (
                  <>
                    <div className="flex items-center gap-1.5">
                      <label className="text-[10px] uppercase tracking-wider opacity-60 font-mono">ETA days</label>
                      <Input
                        type="number" min={1} max={180}
                        value={etaDays[bucket.supplier_name] ?? 30}
                        onChange={(e) => setEtaDays({ ...etaDays, [bucket.supplier_name]: parseInt(e.target.value) || 30 })}
                        className="w-16 h-8 text-sm"
                        data-testid={`eta-input-${bucket.supplier_name}`}
                      />
                    </div>
                    <Button
                      onClick={() => convert(bucket)}
                      disabled={converting === bucket.supplier_name}
                      className="bg-brand hover:bg-brand/90"
                      data-testid={`convert-btn-${bucket.supplier_name}`}
                    >
                      <Truck size={16} className="mr-1" weight="fill" />
                      {converting === bucket.supplier_name ? 'Converting…' : 'Convert to PO'}
                    </Button>
                  </>
                )}
              </div>

              {/* Line items */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted text-[10px] uppercase tracking-wider opacity-70">
                    <tr>
                      <th className="text-left px-3 py-2">SKU</th>
                      <th className="text-right px-3 py-2">Orig qty</th>
                      <th className="text-right px-3 py-2">MOQ</th>
                      <th className="text-right px-3 py-2">Rounded qty</th>
                      <th className="text-right px-3 py-2">Unit $</th>
                      <th className="text-right px-3 py-2">Line total</th>
                      <th className="text-left px-3 py-2">Scenario</th>
                      <th className="text-right px-3 py-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bucket.lines.map(line => (
                      <tr key={line.id} className="border-t border-slate-100" data-testid={`line-${line.sku_code}`}>
                        <td className="px-3 py-2">
                          <div className="font-mono font-bold text-xs">{line.sku_code}</div>
                          <div className="text-[11px] opacity-70 truncate max-w-xs">{line.sku_name}</div>
                        </td>
                        <td className="px-3 py-2 text-right font-mono opacity-70">{line.original_qty}</td>
                        <td className="px-3 py-2 text-right font-mono text-xs opacity-60">{line.moq}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold">{line.moq_rounded_qty}</td>
                        <td className="px-3 py-2 text-right font-mono opacity-70">${line.unit_price.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right font-mono font-bold">
                          ${(line.moq_rounded_qty * line.unit_price).toFixed(2)}
                        </td>
                        <td className="px-3 py-2 text-[11px] opacity-60 truncate max-w-[140px]">
                          {line.scenario_name || '—'}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="sm" variant="outline"
                            onClick={() => openAssign(line.id)}
                            className="h-7 text-xs"
                            data-testid={`assign-btn-${line.sku_code}`}
                          >
                            {unassigned ? 'Assign supplier' : 'Move supplier'}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Assign-supplier modal */}
      {assignModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setAssignModal(null)}>
          <Card className="p-5 max-w-md w-full rounded-none" onClick={(e) => e.stopPropagation()} data-testid="assign-modal">
            <h3 className="text-lg font-bold mb-3">
              Assign supplier
            </h3>
            <p className="text-sm opacity-70 mb-3">
              Enter a supplier name. Drafts with this name will consolidate into a single PO.
            </p>
            <Input
              value={newSupplier}
              onChange={(e) => setNewSupplier(e.target.value)}
              placeholder="e.g. ACME Logistics Co."
              data-testid="assign-supplier-input"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && saveAssign()}
            />
            <div className="flex gap-2 mt-4 justify-end">
              <Button variant="outline" onClick={() => setAssignModal(null)}>Cancel</Button>
              <Button onClick={saveAssign} className="bg-brand hover:bg-brand/90" data-testid="assign-save-btn">
                <CheckCircle size={14} className="mr-1" weight="fill" /> Save
              </Button>
            </div>
          </Card>
        </div>
      )}

      <div className="flex items-center justify-between text-xs opacity-60 font-mono pt-2 border-t border-border">
        <span>Tip: click "Assign supplier" on Unassigned drafts, then "Convert to PO" consolidates all drafts for that vendor into one inbound container.</span>
        <Button variant="outline" size="sm" onClick={load} data-testid="consolidate-refresh">
          <ArrowsClockwise size={14} className="mr-1" /> Refresh
        </Button>
      </div>
    </div>
  );
}
